/**
 * Home page - landing page for Coach Web UI
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import sessionService from '../services/sessionService';

const HomePage: React.FC = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();

  const handleStartAnalysis = async () => {
    setIsCreating(true);
    setError(null);

    try {
      const session = await sessionService.createSession('Untitled Session');
      navigate(`/context/${session.id}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create session');
      setIsCreating(false);
    }
  };

  const primaryBtnStyle: React.CSSProperties = {
    padding: '16px 32px',
    fontSize: '1.1rem',
    backgroundColor: isCreating ? '#ccc' : '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: isCreating ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s',
    fontWeight: 'bold',
  };

  const outlineBtnStyle: React.CSSProperties = {
    padding: '16px 32px',
    fontSize: '1.1rem',
    backgroundColor: 'transparent',
    color: '#007bff',
    border: '2px solid #007bff',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: 'bold',
  };

  const greeting = user?.display_name || user?.email || 'there';

  return (
    <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
      <header style={{ textAlign: 'center', marginBottom: '40px' }}>
        <img
          src="/coach_logo.png"
          alt="Coach — Guide. Plan. Succeed."
          style={{ maxWidth: '320px', width: '100%', marginBottom: '16px' }}
        />
        <p style={{ fontSize: '1.1rem', color: '#666' }}>
          Get comprehensive strategic insights for your business with AI-powered specialist agents
        </p>
      </header>

      {/* Auth-aware action area */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        {isLoading ? (
          <p style={{ color: '#888' }}>Loading...</p>
        ) : isAuthenticated ? (
          <>
            <p style={{ fontSize: '1.1rem', color: '#444', marginBottom: '24px' }}>
              Welcome back, <strong>{greeting}</strong>! Ready to gain new strategic insights?
            </p>

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleStartAnalysis}
                disabled={isCreating}
                style={primaryBtnStyle}
                onMouseEnter={(e) => {
                  if (!isCreating) e.currentTarget.style.backgroundColor = '#0056b3';
                }}
                onMouseLeave={(e) => {
                  if (!isCreating) e.currentTarget.style.backgroundColor = '#007bff';
                }}
              >
                {isCreating ? 'Creating Session...' : 'New Analysis'}
              </button>

              <button
                onClick={() => navigate('/sessions')}
                style={outlineBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#007bff';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#007bff';
                }}
              >
                My Sessions
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: '1.1rem', color: '#444', marginBottom: '24px' }}>
              Sign in to start your AI-powered strategic analysis.
            </p>

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link
                to="/login"
                style={{
                  ...primaryBtnStyle,
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                Log In
              </Link>

              <Link
                to="/signup"
                style={{
                  ...outlineBtnStyle,
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                Sign Up
              </Link>
            </div>
          </>
        )}

        {error && (
          <p style={{ color: 'red', marginTop: '16px' }}>
            Error: {error}
          </p>
        )}
      </div>

      {/* How It Works — always visible */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '16px' }}>How It Works</h2>
        <ol style={{ lineHeight: '1.8' }}>
          <li>Describe your business context, objectives, and timeline</li>
          <li>Our AI selects 3-5 specialist agents relevant to your needs</li>
          <li>Answer questions from each specialist agent</li>
          <li>Receive comprehensive strategic analysis reports</li>
          <li>Download reports as markdown files for your records</li>
        </ol>
      </section>
    </div>
  );
};

export default HomePage;
